/**
 * Плинко: котик скачет по колышкам вниз, слот выбирает хеш.
 *
 * На каждом ряду котик уходит влево или вправо. Двенадцать рядов дают тринадцать
 * корзин. Направление шага берётся из очередного бита хеша, поэтому путь
 * однозначно восстанавливается из тех же сидов, что и выплата.
 *
 * Множители симметричны: края редкие и жирные, середина частая и скромнее.
 * Отдача считается через биномиальные коэффициенты — при честной монете на
 * каждом ряду это точное распределение, а не симуляция.
 */
import { computePayout } from "../money.js";
export const PLINKO_ROWS = 11;
/**
 * Множители корзин слева направо, в сотых долях.
 *
 * Прежняя таблица (5 / 2.5 / 1 / 0.75 / 0.5 / 0.25) отдавала 48% и платила
 * больше ставки только в 1.2% дропов: центр забирал почти половину шаров
 * по четверти ставки. Ближние корзины теперь 1.2x и 1.5x, центр 0.5x —
 * выигрыш примерно в каждом пятом броске, отдача 78.90%. Края по-прежнему 5x.
 */
export const PLINKO_MULTIPLIERS = [
    500, 250, 150, 120, 80, 50, 50, 80, 120, 150, 250, 500,
];
/**
 * Строит путь по первым битам хеша.
 *
 * Берём 52-битную выборку и читаем младшие биты: так не нужно резать хеш на
 * куски разной длины, и проверка игроком сводится к одной арифметической операции.
 */
export function dropPlinko(hashHex, rows = PLINKO_ROWS) {
    if (rows <= 0 || rows > 52) {
        throw new Error(`Число рядов плинко вне диапазона: ${rows}`);
    }
    const slice = hashHex.slice(0, 13);
    if (slice.length < 13)
        throw new Error("Хеш слишком короткий для плинко");
    let bits = BigInt(`0x${slice}`);
    const path = [];
    let bucket = 0;
    for (let row = 0; row < rows; row += 1) {
        const step = Number(bits & 1n);
        path.push(step);
        bucket += step;
        bits >>= 1n;
    }
    const multipliers = plinkoMultipliers(rows);
    const multiplier = multipliers[bucket];
    if (multiplier === undefined) {
        throw new Error(`Корзина ${bucket} не существует при ${rows} рядах`);
    }
    return { path, bucket, multiplier };
}
export function plinkoPayout(stake, multiplier) {
    return computePayout(stake, multiplier);
}
export function plinkoMultipliers(rows = PLINKO_ROWS) {
    if (rows === PLINKO_ROWS)
        return PLINKO_MULTIPLIERS;
    throw new Error(`Для ${rows} рядов нет таблицы множителей`);
}
/** Самая жирная корзина: по ней считается риск казны на одну ставку. */
export function plinkoMaxMultiplier(rows = PLINKO_ROWS) {
    return plinkoMultipliers(rows).reduce((max, multiplier) => Math.max(max, multiplier), 0);
}
/** Биномиальный коэффициент C(n, k) через целую арифметику. */
export function binomial(n, k) {
    if (k < 0 || k > n)
        return 0n;
    const take = Math.min(k, n - k);
    let result = 1n;
    for (let i = 0; i < take; i += 1) {
        result = (result * BigInt(n - i)) / BigInt(i + 1);
    }
    return result;
}
/**
 * Точная отдача плинко: сумма C(rows, k) * multiplier(k) / 2^rows.
 * Множители в сотых, результат — в базисных пунктах.
 */
export function plinkoRtpBp(rows = PLINKO_ROWS) {
    const multipliers = plinkoMultipliers(rows);
    if (multipliers.length !== rows + 1) {
        throw new Error("Число корзин должно быть на одну больше числа рядов");
    }
    const total = 1n << BigInt(rows);
    let weighted = 0n;
    for (let bucket = 0; bucket <= rows; bucket += 1) {
        const multiplier = multipliers[bucket];
        if (multiplier === undefined)
            throw new Error(`Нет множителя для корзины ${bucket}`);
        weighted += binomial(rows, bucket) * BigInt(multiplier);
    }
    return Number((weighted * 100n) / total);
}
export function assertPlinkoIsSane(rows = PLINKO_ROWS, maxRtpBp = 9900) {
    const rtp = plinkoRtpBp(rows);
    if (rtp > maxRtpBp) {
        throw new Error(`Плинко отдаёт ${(rtp / 100).toFixed(2)}% при допустимых ${(maxRtpBp / 100).toFixed(2)}%`);
    }
    const multipliers = plinkoMultipliers(rows);
    if (!multipliers.some((multiplier) => multiplier > 100)) {
        throw new Error("В плинко нет корзины дороже ставки");
    }
}
//# sourceMappingURL=plinko.js.map